// frontend/src/hooks/useAuth.js
import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { StorageService } from '../services/StorageService';
import { DataService } from '../services/DataService';
import { Capacitor } from '@capacitor/core';
import * as IAPService from '../services/IAPService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(StorageService.get('token'));
  const [refreshToken, setRefreshToken] = useState(StorageService.get('refreshToken'));
  const [currentLabId, setCurrentLabId] = useState(parseInt(StorageService.get('currentLabId')) || null);
  const [currentViewMode, setCurrentViewMode] = useState(StorageService.get('currentViewMode') || 'personal');
  const [authError, setAuthError] = useState(null);
  // isRestored tracks whether Capacitor Preferences has been synced into localStorage.
  // On web, StorageService.restore() is a no-op and resolves immediately (true from start).
  // On iOS, it's false until restore() finishes, preventing the auth check from running
  // against an empty localStorage before Preferences values have been loaded.
  const [isRestored, setIsRestored] = useState(false);

  // Refs so the interceptors always read the latest values without stale closures.
  // tokenRef is also updated synchronously at every token change (login, refresh
  // success, logout) because the request interceptor would otherwise re-stamp
  // requests retried right after a refresh with the old expired token — the
  // state update doesn't re-run the interceptor until the next re-render.
  const tokenRef = useRef(token);
  const refreshTokenRef = useRef(refreshToken);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { refreshTokenRef.current = refreshToken; }, [refreshToken]);

  // On iOS, restore persisted keys from Capacitor Preferences into localStorage
  // in case WKWebView cleared localStorage. Runs once at boot.
  // Must complete before the auth check effect runs.
  useEffect(() => {
    StorageService.restore(['token', 'refreshToken', 'user', 'currentLabId', 'currentViewMode', 'showWelcome'])
      .then(() => {
        // Re-read from localStorage in case restore() populated values that were missing
        const restoredToken = localStorage.getItem('token');
        const restoredRefreshToken = localStorage.getItem('refreshToken');
        const restoredLabId = localStorage.getItem('currentLabId');
        const restoredViewMode = localStorage.getItem('currentViewMode');
        setToken(prev => prev || restoredToken || null);
        setRefreshToken(prev => prev || restoredRefreshToken || null);
        setCurrentLabId(prev => prev || parseInt(restoredLabId) || null);
        setCurrentViewMode(prev => prev || restoredViewMode || 'personal');
        setIsRestored(true);
      })
      .catch(() => {
        setIsRestored(true); // Ensure auth check still runs even if restore fails
      });
  }, []);

  // Set up axios interceptors
  useEffect(() => {
    // Interceptor to add the auth token to requests — reads the ref so
    // requests (including retries after a token refresh) always carry the
    // newest token without waiting for a re-render
    const tokenInterceptor = axios.interceptors.request.use(config => {
      if (tokenRef.current) {
        config.headers['Authorization'] = `Bearer ${tokenRef.current}`;
      }
      return config;
    });

    // Interceptor to add the view mode to requests
    const viewModeInterceptor = axios.interceptors.request.use(config => {
      config.headers['x-view-mode'] = currentViewMode;
      return config;
    });

    return () => {
      axios.interceptors.request.eject(tokenInterceptor);
      axios.interceptors.request.eject(viewModeInterceptor);
    };
  }, [currentViewMode]);

  // Response interceptor: on 401, silently refresh the token pair and retry the request.
  // Uses refs so it doesn't need to be recreated when token state changes.
  useEffect(() => {
    let isRefreshing = false;
    let failedQueue = [];
    const processQueue = (error, newToken = null) => {
      failedQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(newToken));
      failedQueue = [];
    };

    const responseInterceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;
        const url = originalRequest.url || '';
        // Public auth endpoints report their own failures as 401 (wrong
        // password, bad reset token, ...) and the calling component displays
        // the error. Running them through the refresh/logout flow would log
        // the user out and reload the page, wiping the error message.
        const isPublicAuthCall =
          url.includes('/api/auth/login') ||
          url.includes('/api/auth/register') ||
          url.includes('/api/auth/forgot-password') ||
          url.includes('/api/auth/reset-password');
        // Only intercept 401s that haven't been retried and aren't from the
        // refresh endpoint or a public auth endpoint
        if (
          error.response?.status !== 401 ||
          originalRequest._retry ||
          isPublicAuthCall ||
          url.includes('/api/auth/refresh')
        ) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(newToken => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const currentRefreshToken = refreshTokenRef.current;
        if (!currentRefreshToken) {
          isRefreshing = false;
          window.dispatchEvent(new CustomEvent('squeakbase-session-expired'));
          logout();
          return Promise.reject(error);
        }

        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/auth/refresh`,
            { refreshToken: currentRefreshToken }
          );
          const { token: newToken, refreshToken: newRefreshToken } = resp.data;

          // Update the ref synchronously, BEFORE the retried requests below
          // dispatch — the request interceptor reads tokenRef.current, and the
          // setToken re-render hasn't happened yet.
          tokenRef.current = newToken;
          setToken(newToken);
          setRefreshToken(newRefreshToken);
          StorageService.set('token', newToken);
          StorageService.set('refreshToken', newRefreshToken);

          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          isRefreshing = false;

          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;
          // Surface why the user is being logged out (refresh token revoked or
          // expired) before the redirect to /login
          window.dispatchEvent(new CustomEvent('squeakbase-session-expired'));
          logout();
          return Promise.reject(refreshError);
        }
      }
    );

    return () => axios.interceptors.response.eject(responseInterceptor);
  }, []); // Empty deps — reads latest values via refs

  // New useEffect to capture join_code on initial load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const joinCode = urlParams.get('join_code');
    if (joinCode) {
      sessionStorage.setItem('join_code', joinCode);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // Runs only once on component mount

  const logout = useCallback(async () => {
    // Best-effort: tell the server to revoke the refresh token
    const currentRefreshToken = refreshTokenRef.current;
    if (currentRefreshToken) {
      axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`, { refreshToken: currentRefreshToken })
        .catch(() => {}); // fire-and-forget
    }
    setUser(null);
    tokenRef.current = null;
    setToken(null);
    setRefreshToken(null);
    setCurrentLabId(null);
    setCurrentViewMode('personal');
    StorageService.remove('token');
    StorageService.remove('refreshToken');
    StorageService.remove('currentLabId');
    StorageService.remove('currentViewMode');
    StorageService.remove('user');
    StorageService.remove('showWelcome');
    delete axios.defaults.headers.common['Authorization'];
    // Await the cache clear before navigating — the redirect tears down the
    // page, and on WKWebView that aborts in-flight IndexedDB transactions,
    // leaving the next account to see this account's data.
    try {
      await DataService.clearAllData();
    } catch (err) {
      console.error('Failed to clear local cache on logout:', err);
    }
    window.location.href = '/login';
  }, []);


  // Check if user is logged in on app start.
  // Waits for isRestored so the auth check always sees the correct token,
  // even on iOS where WKWebView may have cleared localStorage before restore() ran.
  useEffect(() => {
    if (!isRestored) return;
    const checkAuthAndJoinLab = async () => {
      if (token) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`);
          const fetchedUser = response.data.user;
          const fullUser = { ...fetchedUser, subscription_tier: fetchedUser.subscription_tier || 'free' };

          // Wipe any cached Dexie data left behind by a different account
          // before anything renders and reads it. Best-effort: a failure here
          // leaves the old stamp in place so it's retried on the next check.
          await DataService.ensureCacheOwner(fetchedUser.id)
            .catch(err => console.error('Cache ownership check failed:', err));

          setUser(fullUser);
          StorageService.set('user', JSON.stringify(fullUser));

          // On iOS: sync StoreKit entitlements with the backend.
          // If StoreKit reports an active subscription but the backend shows 'free'
          // (e.g. after a fresh install or reinstall), re-verify the transaction so
          // the tier is restored without requiring the user to re-purchase.
          if (Capacitor.isNativePlatform()) {
            try {
              const entitlements = await IAPService.getCurrentEntitlements();
              const backendTier = fullUser.subscription_tier || 'free';
              const hasActiveAppleSubscription = entitlements.length > 0;
              if (hasActiveAppleSubscription && backendTier === 'free') {
                await axios.post(
                  `${process.env.REACT_APP_API_URL}/api/apple/verify-transaction`,
                  { signedTransaction: entitlements[0].signedTransaction }
                );
                // Refresh user data to pick up the restored tier
                const refreshed = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`);
                const refreshedUser = { ...refreshed.data.user, subscription_tier: refreshed.data.user.subscription_tier || 'free' };
                setUser(refreshedUser);
                StorageService.set('user', JSON.stringify(refreshedUser));
              }
            } catch {
              // Entitlement sync is best-effort; don't block login on failure
            }
          }

          if (fetchedUser.lab_id){
            setCurrentLabId(fetchedUser.lab_id);
            StorageService.set('currentLabId', String(fetchedUser.lab_id));
          }
          // Store Transnetyx integration status
          if (fetchedUser.has_tyx_integration) {
            setUser(prevUser => ({
              ...prevUser,
              has_tyx_integration: fetchedUser.has_tyx_integration,
              tyx_expires_at: fetchedUser.tyx_expires_at
            }));
          }

          // Now, check for and process a stored join_code
          const joinCode = sessionStorage.getItem('join_code');
          if (joinCode && !fetchedUser.lab_id) { // Only try to join if a code exists and user is not already in a lab
            try {
              await axios.post(`${process.env.REACT_APP_API_URL}/api/labs/join`, { join_code: joinCode });
              sessionStorage.removeItem('join_code');
              // Success! Reload the page to get the new user context with the lab_id.
              window.location.reload();
            } catch (joinError) {
              console.error('Failed to join lab with code:', joinError);
              setAuthError(joinError.response?.data?.error || 'Invalid or expired lab invitation code.');
              sessionStorage.removeItem('join_code');
            }
          }


        } catch (error) {
          console.error('Auth check failed:', error);
          if (!error.response) { // Network error usually has no response
             const cachedUser = StorageService.get('user');
             if (cachedUser) {
                 try {
                   const parsedUser = JSON.parse(cachedUser);
                   await DataService.ensureCacheOwner(parsedUser.id)
                     .catch(err => console.error('Cache ownership check failed:', err));
                   setUser(parsedUser);
                   console.log('Offline mode: Loaded user from cache');
                 } catch {
                   console.error('Cached user data is corrupted, logging out');
                   logout();
                 }
             } else {
                 logout();
             }
          } else {
             logout();
          }
        }
      }
      setLoading(false);
    };

    checkAuthAndJoinLab();
  }, [token, logout, isRestored]);

  const login = async (username, password) => {
    setAuthError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        username,
        password
      });
      
      const { user, token, refreshToken: newRefreshToken } = response.data;
      // Clear a previous account's cached Dexie data before the token/user
      // state lands and components (or background sync) start reading it.
      await DataService.ensureCacheOwner(user.id)
        .catch(err => console.error('Cache ownership check failed:', err));
      setUser(user);
      tokenRef.current = token;
      setToken(token);
      setRefreshToken(newRefreshToken || null);
      if (user.lab_id){
        setCurrentLabId(user.lab_id);
        StorageService.set('currentLabId', String(user.lab_id));
      }
      StorageService.set('token', token);
      if (newRefreshToken) StorageService.set('refreshToken', newRefreshToken);
      StorageService.set('user', JSON.stringify(user));

      const urlParams = new URLSearchParams(window.location.search);
      const joinCode = urlParams.get('join_code');
      if (joinCode) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/labs/join`, { join_code: joinCode });
        window.location.href = '/';
      }


      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    setAuthError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/register`, userData);

      const { user, token, refreshToken: newRefreshToken } = response.data;
      // A brand-new account must never inherit another account's cached data.
      await DataService.ensureCacheOwner(user.id)
        .catch(err => console.error('Cache ownership check failed:', err));
      setUser(user);
      tokenRef.current = token;
      setToken(token);
      setRefreshToken(newRefreshToken || null);
      if (user.lab_id){
        setCurrentLabId(user.lab_id);
        StorageService.set('currentLabId', String(user.lab_id));
      }
      StorageService.set('token', token);
      if (newRefreshToken) StorageService.set('refreshToken', newRefreshToken);
      StorageService.set('user', JSON.stringify(user));

      const urlParams = new URLSearchParams(window.location.search);
      const joinCode = urlParams.get('join_code');
      if (joinCode) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/labs/join`, { join_code: joinCode });
        window.location.href = '/';
      }


      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const switchViewMode = (mode) => {
      setCurrentViewMode(mode);
      StorageService.set('currentViewMode', mode);
  };

  // Merge server-confirmed field changes into the cached user (C8).
  // Settings toggles kept their new value in local component state only, so
  // anything else reading it off `user` — the dashboard's weaning cutoff, for
  // one — went on using the stale value until the next full page load. Only
  // call this with values the server has already accepted.
  const updateUser = (fields) => {
    setUser(prevUser => {
      if (!prevUser) return prevUser;
      const merged = { ...prevUser, ...fields };
      StorageService.set('user', JSON.stringify(merged));
      return merged;
    });
  };

  const value = {
    user,
    loading,
    token, // Expose token so components can use it directly for requests
    currentLabId,
    currentViewMode,
    login,
    register,
    logout,
    switchViewMode,
    updateUser,
    isAuthenticated: !!user,
    authError,
    clearAuthError: () => setAuthError(null)  
};

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};